import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { AllCommunityModule, ColDef, ModuleRegistry } from "ag-grid-community";
import { MasterDetailModule } from "ag-grid-enterprise";
import clsx from "clsx";
import { LicenseManager } from "ag-grid-enterprise";
import { investmentMemoGridTheme } from "./agGridTheme";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { useSetAtom } from "jotai";
import { showInvestmentCriteriaDrawerAtom } from "../state";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getInvestmentCriteria } from "@/entities/CreditBox/CreditBox.queries";
import { CreditBoxResult } from "@/entities/CreditBox/CreditBox.types";
import { getSFDCData } from "@/entities/Deal/Deal.queries";
import {
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";

LicenseManager.setLicenseKey(process.env.AG_LICENSE_KEY || "");

ModuleRegistry.registerModules([AllCommunityModule, MasterDetailModule]);

type CriteriaDetailRow = {
    description: string;
    opportunity: string;
    criteria: string;
    score: number | null;
    maxScore: number | null;
};

type InvestmentCriteriaRow = {
    creditBoxName: string;
    capitalProductType: string | null;
    score: number;
    value: CreditBoxResult;
    lastPull: string;
    criteria: CriteriaDetailRow[];
    hasValidFormat: boolean;
};

const ScoreCellRenderer = (props: any) => {
    const score = props.data?.score;
    const maxScore = props.data?.maxScore;

    if (maxScore === null || maxScore === undefined) {
        return <span>-</span>;
    }

    const actualScore = score ?? 0;

    const totalBars = Math.abs(Math.round(maxScore));
    const filledBars = Math.round(actualScore);
    const percentage = maxScore > 0 ? (actualScore / maxScore) * 100 : 0;

    const getColorClass = () => {
        if (actualScore < 0) {
            if (percentage <= -80) return "bg-red-500";
            if (percentage <= -60) return "bg-red-300";
            if (percentage <= -40) return "bg-red-200";
            if (percentage <= -20) return "bg-red-100";
            return "bg-red-50";
        }

        if (percentage >= 80) return "bg-emerald-500";
        if (percentage >= 60) return "bg-emerald-400";
        if (percentage >= 40) return "bg-emerald-300";
        if (percentage >= 20) return "bg-emerald-200";
        if (actualScore > 0) return "bg-emerald-100";
        return "bg-slate-700";
    };

    const colorClass = getColorClass();

    return (
        <div
            className={clsx(
                "flex w-full items-center px-4 py-3",
                actualScore === null ||
                    actualScore === undefined ||
                    actualScore === 0
                    ? "justify-start"
                    : actualScore > 0
                      ? "justify-start"
                      : "justify-end",
            )}
        >
            <div className="flex items-center gap-0.5">
                {actualScore > 0 && <div className="h-5 w-px bg-white" />}
                {Array.from({ length: totalBars }).map((_, i) => {
                    const isBarFilled =
                        filledBars >= 0
                            ? i < filledBars
                            : i < Math.abs(filledBars);

                    return (
                        <div
                            key={i}
                            className={clsx(
                                "h-4 w-0.5",
                                isBarFilled ? colorClass : "bg-slate-700",
                            )}
                        />
                    );
                })}
                {actualScore < 0 && <div className="h-5 w-px bg-white" />}
            </div>
        </div>
    );
};

const ValueBadgeCellRenderer = (props: any) => {
    const value = props.value;

    if (!value) {
        return <span>-</span>;
    }

    const valueColors = {
        [CreditBoxResult.PASS]: "bg-green-200",
        [CreditBoxResult.FAIL]: "bg-red-200",
    };
    const colorClass = valueColors[value as CreditBoxResult] || "bg-gray-200";

    return (
        <div
            className={clsx(
                "lozenge w-max rounded-xl px-2 py-1 text-brand-900",
                colorClass,
            )}
        >
            {value}
        </div>
    );
};

const useInnerTableParams = () => {
    return useMemo(
        () => ({
            detailGridOptions: {
                theme: investmentMemoGridTheme,
                columnDefs: [
                    {
                        field: "description",
                        headerName: "Description",
                        flex: 2,
                        minWidth: 200,
                        wrapText: true,
                        autoHeight: true,
                        cellStyle: {
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            lineHeight: "1.4",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                        },
                    },
                    {
                        field: "opportunity",
                        headerName: "Opportunity",
                        flex: 1.5,
                        minWidth: 150,
                        wrapText: true,
                        autoHeight: true,
                        cellStyle: {
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            lineHeight: "1.4",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                        },
                    },
                    {
                        field: "criteria",
                        headerName: "Criteria",
                        flex: 1.5,
                        minWidth: 150,
                        wrapText: true,
                        autoHeight: true,
                        cellStyle: {
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "normal",
                            wordWrap: "break-word",
                            lineHeight: "1.4",
                            paddingTop: "12px",
                            paddingBottom: "12px",
                        },
                    },
                    {
                        field: "score",
                        headerName: "Score",
                        resizable: false,
                        flex: 1,
                        minWidth: 120,
                        cellRenderer: ScoreCellRenderer,
                        wrapText: true,
                        autoHeight: true,
                    },
                ],
                defaultColDef: {
                    flex: 1,
                    wrapText: true,
                    autoHeight: true,
                    wrapHeaderText: true,
                    autoHeaderHeight: true,
                    cellStyle: {
                        paddingTop: "12px",
                        paddingBottom: "12px",
                    },
                },
            },
            getDetailRowData: (params: any) => {
                const criteria = params.data?.criteria || [];
                params.successCallback(criteria);
            },
        }),
        [],
    );
};

const onFirstDataRendered = (params: any) => {
    const firstRow = params.api.getRowNode("0");

    if (firstRow && firstRow.data?.hasValidFormat) {
        firstRow.setExpanded(true);
    }
};

const formatValue = (
    value: number | string | null,
    featureName?: string,
): string => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "string") {
        if (value === "None") return "None";
        return value;
    }
    if (typeof value === "number") {
        if (
            value === 0 &&
            (featureName === "Software Only" ||
                featureName === "Has Recent Equity Raise")
        ) {
            return "0";
        }

        if (featureName === "Equity Sponsorship") {
            return formatFinanceLabValue(value, FinanceLabFieldType.CURRENCY);
        }

        if (featureName === "Self-Reported Annualized EBITDA") {
            return formatFinanceLabValue(value, FinanceLabFieldType.CURRENCY);
        }

        if (
            featureName === "Pro Forma debt to Revenue" ||
            featureName === "Pro Forma debt to EBITDA"
        ) {
            return formatFinanceLabValue(value, FinanceLabFieldType.MULTIPLE);
        }

        if (value >= 1000) {
            return formatFinanceLabValue(value, FinanceLabFieldType.CURRENCY);
        }

        if (featureName === "Annual Revenue Growth %") {
            return formatFinanceLabValue(value, FinanceLabFieldType.PERCENTAGE);
        }

        if (value < 1 && value > -1) {
            return formatFinanceLabValue(value, FinanceLabFieldType.PERCENTAGE);
        }
        return formatFinanceLabValue(value, FinanceLabFieldType.INTEGER);
    }
    return String(value);
};

const hasValidDetailFormat = (detail: any): boolean => {
    return (
        "featureName" in detail &&
        "operator" in detail &&
        "opportunityValue" in detail &&
        "creditBoxValue" in detail
    );
};

export function InvestmentCriteriaDrawer() {
    const setShowInvestmentCriteriaDrawer = useSetAtom(
        showInvestmentCriteriaDrawerAtom,
    );
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);

    const sfdcData = useApiQuery(getSFDCData);
    const sfdcOpportunityId = sfdcData.data?.[0]?.id;

    const { data: criteriaData, isLoading } = useApiQuery(
        getInvestmentCriteria,
        {
            params: { sfdcOpportunityId: sfdcOpportunityId || "" },
            enabled: !!sfdcOpportunityId,
        },
    );

    const tableData = useMemo<InvestmentCriteriaRow[]>(() => {
        if (!criteriaData?.creditBoxResultDetails) return [];

        return criteriaData.creditBoxResultDetails
            .map((result) => {
                const criteriaDetails = result.details[0]?.details || [];

                const hasValidFormat =
                    criteriaDetails.length > 0 &&
                    hasValidDetailFormat(criteriaDetails[0]);

                const capitalProductCriteria = criteriaDetails.find(
                    (d) => d.featureName === "Capital Product Type",
                );

                const criteria: CriteriaDetailRow[] = hasValidFormat
                    ? criteriaDetails.map((detail) => {
                          return {
                              description: detail.featureName,
                              opportunity: formatValue(
                                  detail.opportunityValue,
                                  detail.featureName,
                              ),
                              criteria: `${detail.operator} ${formatValue(detail.creditBoxValue, detail.featureName)}`,
                              score: detail.creditBoxScore,
                              maxScore: detail.creditBoxBestPossibleScore,
                          };
                      })
                    : [];

                return {
                    creditBoxName: result.creditBoxName,
                    capitalProductType:
                        (capitalProductCriteria?.creditBoxValue as string) ||
                        null,
                    score: result.score,
                    value:
                        result.score > 0
                            ? CreditBoxResult.PASS
                            : CreditBoxResult.FAIL,
                    lastPull: result.updatedAt,
                    criteria,
                    hasValidFormat,
                };
            })
            .sort((a, b) => {
                if (
                    a.value === CreditBoxResult.PASS &&
                    b.value === CreditBoxResult.FAIL
                )
                    return -1;
                if (
                    a.value === CreditBoxResult.FAIL &&
                    b.value === CreditBoxResult.PASS
                )
                    return 1;
                return 0;
            });
    }, [criteriaData]);

    const InnerTableParams = useInnerTableParams();

    const handleClose = () => {
        setShowInvestmentCriteriaDrawer(false);
        setRightDrawerOpen(false);
    };

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                field: "creditBoxName",
                headerName: "Strategy Name",
                resizable: false,
                flex: 2,
                minWidth: 200,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                },
            },
            {
                field: "capitalProductType",
                headerName: "Capital Product Type",
                resizable: false,
                flex: 1.5,
                minWidth: 180,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                },
            },
            {
                field: "score",
                headerName: "Score",
                resizable: false,
                flex: 0.8,
                minWidth: 100,
                wrapText: true,
                autoHeight: true,
                valueFormatter: ({ value }) => {
                    return value?.toFixed(1) || "-";
                },
                cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                },
            },
            {
                field: "value",
                headerName: "Value",
                resizable: false,
                flex: 1,
                minWidth: 120,
                cellRenderer: ValueBadgeCellRenderer,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                },
            },
            {
                field: "lastPull",
                headerName: "Last Pull",
                resizable: false,
                flex: 1,
                minWidth: 140,
                wrapText: true,
                autoHeight: true,
                valueFormatter: ({ value }) => {
                    if (!value) return "-";
                    const date = new Date(value);
                    return date.toLocaleDateString("en-US", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                    });
                },
                cellStyle: {
                    display: "flex",
                    alignItems: "center",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    paddingTop: "12px",
                    paddingBottom: "12px",
                },
            },
            {
                field: "expand",
                headerName: "",
                cellRendererSelector: (params: any) => {
                    if (params.data?.hasValidFormat) {
                        return {
                            component: "agGroupCellRenderer",
                            params: {
                                suppressCount: true,
                                innerRenderer: () => "",
                            },
                        };
                    }
                    return undefined;
                },
                resizable: false,
                flex: 0.3,
                minWidth: 50,
                maxWidth: 80,
                pinned: "right",
                lockPinned: true,
            },
        ],
        [],
    );

    const defaultColDef = useMemo<ColDef>(
        () => ({
            flex: 1,
            wrapText: true,
            autoHeight: true,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            cellStyle: {
                paddingTop: "12px",
                paddingBottom: "12px",
            },
        }),
        [],
    );

    return (
        <div className={clsx("bg-default", "flex size-full flex-col gap-4")}>
            <div className="pt-4">
                <RightSidebarButton
                    label="Close"
                    iconDirection="right"
                    onClick={handleClose}
                />
            </div>
            <div className="size-full">
                <h1 className="pageheading mb-1">Investment Criteria</h1>
                <p className="subdued body-s mb-6">
                    Drill down by strategy to see how this opportunity scores
                    against each investment criteria and its associated
                    importance in the matching schema. The score column shows
                    how well an opportunity matches your criteria. More color
                    means a stronger match; more bars indicate higher
                    importance.{" "}
                    <a
                        href="https://info.humcapital.com/hum-help-center/investment-criteria-match-scores"
                        target="_blank"
                        rel="noreferrer"
                        className="text-link"
                    >
                        Read more.
                    </a>
                </p>
                <div className="mt-6 size-full">
                    {isLoading ? (
                        <div className="h-64 animate-pulse rounded-xl bg-slate-800" />
                    ) : tableData && tableData.length > 0 ? (
                        <AgGridReact<InvestmentCriteriaRow>
                            theme={investmentMemoGridTheme}
                            rowData={tableData}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            masterDetail={true}
                            isRowMaster={(dataItem) => dataItem.hasValidFormat}
                            detailCellRendererParams={InnerTableParams}
                            onFirstDataRendered={onFirstDataRendered}
                            detailRowAutoHeight
                            onRowGroupOpened={(params) => {
                                const api = params.api;
                                const currentRowNode = params.node;

                                api.forEachNode((node) => {
                                    if (node.id !== currentRowNode.id) {
                                        node.setExpanded(false);
                                    }
                                });
                            }}
                            domLayout="autoHeight"
                        />
                    ) : (
                        <div className="flex items-center justify-center py-12">
                            <p className="subdued body-s">No data available</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
